// File: frontend/src/hooks/useMatchSelection.ts
// hooks/useMatchSelection.ts
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { generateMatchSlug, extractMatchIdFromSlug } from "../utils/slugGenerator";
import { withCurrentQueryAndHash } from "../utils/canonicalUrl";
import { MatchModel } from "../models";
import { fetchMatchById } from "../services/matchService";
import { MergedTopicError } from "../services/apiClient";
import { customTopicService } from "../services/customTopicService";
import { onMatchUpdated } from "../services/websocketService";

/**
 * Custom hook for managing match selection and URL synchronization.
 *
 * filterKey is a stable string that represents the user-visible filter state
 * (date range, sport types, match genders, sort order). It must change ONLY
 * when the user explicitly changes the filter — NOT when preferences reload
 * or when "load more" appends extra matches. This drives the "redirect to
 * first match" behaviour correctly without false positives.
 */
export const useMatchSelection = (
  matches: MatchModel[],
  loadingMatches: boolean,
  filterKey: string = ""
) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Search results page: root path with a keyword or any advanced filter param.
  // Filter-only searches (author / date range without a keyword) have no q= in
  // the URL, so they must be detected via the filter params as well — otherwise
  // this hook navigates away from the results page instantly.
  const searchQs = new URLSearchParams(location.search);
  const isSearchResultsUrl =
    location.pathname === "/" &&
    (searchQs.has("q") || searchQs.has("yazar") || searchQs.has("baslangic") || searchQs.has("bitis"));

  const isSpecialPage = location.pathname.startsWith("/yazar/") || 
                        location.pathname.startsWith("/ayarlar") ||
                        location.pathname.startsWith("/admin") ||
                        location.pathname.startsWith("/dis-link") ||
                        location.pathname.startsWith("/cop-kutusu") ||
                        location.pathname.startsWith("/taslaklar") ||
                        location.pathname.startsWith("/moderasyon-gunlugu") ||
                        location.pathname.startsWith("/mesajlar") ||
                        location.pathname.startsWith("/akis") ||
                        // Entry permalink route (/entry/{id}) — it resolves
                        // itself to a topic page; redirecting it to a match
                        // would destroy the id before it can be looked up
                        location.pathname.startsWith("/entry/") ||
                        // Person profile page — must not redirect to a match URL
                        location.pathname.startsWith("/kisi/") ||
                        // Club page — must not redirect to a match URL
                        location.pathname.startsWith("/kulup/") ||
                        // Team page — must not redirect to a match URL
                        location.pathname.startsWith("/takim/") ||
                        // Custom topic page — must not redirect to a match URL
                        location.pathname.startsWith("/gundem/") ||
                        // Search results page: root path with ?q= or any advanced filter param
                        isSearchResultsUrl ||
                        // Statistics page — not a match route
                        location.pathname.startsWith("/istatistikler") ||
                        // Contact page — not a match route
                        location.pathname.startsWith("/iletisim") ||
                        // Password reset page — not a match route
                        location.pathname.startsWith("/sifre-sifirla") ||
                        // Legal pages — not match routes
                        location.pathname.startsWith("/kullanim-kosullari") ||
                        location.pathname.startsWith("/gizlilik-politikasi") ||
                        location.pathname.startsWith("/cerez-politikasi") ||
                        location.pathname.startsWith("/cerez-tercihleri") ||
                        location.pathname.startsWith("/hamur-abi-kurallari");

  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [fetchedMatch, setFetchedMatch] = useState<MatchModel | null>(null);
  // Tracks isLocked for the currently selected match — always fetched fresh from DB
  // so that an admin lock/unlock is immediately reflected without a full list reload.
  const [lockedMatchId, setLockedMatchId] = useState<number | null>(null);
  const [isLockedOverride, setIsLockedOverride] = useState<boolean>(false);
  // Same staleness problem as isLocked above — the list may predate an admin's lock action
  const [lockReasonOverride, setLockReasonOverride] = useState<string | null>(null);

  // Track the filter key that was active at the last redirect-to-first decision.
  // Only updated when loadingMatches transitions from true→false (matches are fresh).
  // Bumped when a real-time update arrives for the selected match while that
  // match is NOT in the left-pane list (deep link, search result, old match).
  // When it IS in the list, useMatches patches the list and the fetch effect
  // below re-runs through its [matches] dependency on its own.
  const [liveRefreshTick, setLiveRefreshTick] = useState(0);
  const selectedMatchIdRef = useRef<number | null>(null);
  const matchesRef = useRef<MatchModel[]>(matches);
  selectedMatchIdRef.current = selectedMatchId;
  matchesRef.current = matches;

  useEffect(() => {
    const unsubscribe = onMatchUpdated(({ matchId }) => {
      if (matchId !== selectedMatchIdRef.current) return;
      if (matchesRef.current.some((m) => m.id === matchId)) return;
      setLiveRefreshTick((t) => t + 1);
    });
    return unsubscribe;
  }, []);

  const prevFilterKeyRef = useRef<string>("");
  // Tracks the previous loadingMatches value so we can detect the true→false transition.
  const wasLoadingRef = useRef<boolean>(true);

  const selectAndNavigateToMatch = (match: MatchModel) => {
    setSelectedMatchId(match.id);
    setFetchedMatch(null);
    const slug = generateMatchSlug(match);
    navigate(`/${slug}`, { replace: false });
  };

  // Guards the async landing-page redirect below: the effect can re-fire while
  // the top-Gündem-topic fetch is still in flight (matches dep / pathname dep),
  // and without this flag every re-run would start another fetch + navigate.
  const gundemRedirectRef = useRef<boolean>(false);

  /**
   * Landing page behaviour (bare "/"): open the topmost Gündem topic in
   * "Son Giriler" order (the custom topic that received the most recent
   * entry) instead of the first match — per product decision, the left pane
   * already defaults to the Gündem tab. Falls back to the classic
   * first-match landing when there is no active topic or the fetch fails.
   */
  const redirectToTopGundemTopic = async (fallbackMatch: MatchModel | null) => {
    if (gundemRedirectRef.current) return;
    gundemRedirectRef.current = true;
    try {
      // Always "lastEntryAt" desc — the landing target follows the "Son
      // Giriler" ordering regardless of the pane's currently selected sort
      const result = await customTopicService.getAllTopics(0, 1, "lastEntryAt", "desc");
      const topTopic = result.content?.[0];
      if (topTopic?.slug) {
        navigate(`/gundem/${topTopic.slug}`, { replace: true });
        return;
      }
    } catch {
      // Backend hiccup — fall through to the classic first-match landing
    } finally {
      gundemRedirectRef.current = false;
    }
    if (fallbackMatch) {
      setSelectedMatchId(fallbackMatch.id);
      navigate(`/${generateMatchSlug(fallbackMatch)}`, { replace: true });
    }
  };

  useEffect(() => {
    // Track whether loading just completed (true→false transition).
    // This is the only moment when `matches` is guaranteed to be fresh for `filterKey`.
    const justFinishedLoading = wasLoadingRef.current && !loadingMatches;
    wasLoadingRef.current = loadingMatches;

    if (loadingMatches) return;

    const filterChanged = filterKey !== prevFilterKeyRef.current && prevFilterKeyRef.current !== "";

    // If filterKey changed but loading hasn't started yet (matches are still stale),
    // skip processing and wait for the load cycle to complete.
    if (filterChanged && !justFinishedLoading) return;

    prevFilterKeyRef.current = filterKey;

    // If the user changed the filter -> always redirect to first match in fresh results
    if (filterChanged) {
      setFetchedMatch(null);
      if (matches.length > 0) {
        const firstMatch = matches[0];
        setSelectedMatchId(firstMatch.id);
        if (!isSpecialPage) {
          const slug = generateMatchSlug(firstMatch);
          navigate(`/${slug}`, { replace: true });
        }
      } else {
        setSelectedMatchId(null);
      }
      return;
    }

    // Check URL for matchId
    const pathSlug = location.pathname.slice(1);
    const matchIdFromUrl = pathSlug ? extractMatchIdFromSlug(pathSlug) : null;

    // If URL has valid matchId -> select it (even if not in list)
    if (matchIdFromUrl !== null) {
      setSelectedMatchId(matchIdFromUrl);
      // If match is in the current list, replace bare /mac--{id} URL with proper slug
      if (!isSpecialPage) {
        const matchInList = matches.find(m => m.id === matchIdFromUrl);
        if (matchInList) {
          const properSlug = generateMatchSlug(matchInList);
          if (location.pathname !== `/${properSlug}`) {
            // Keep "?sayfa=" and "#entry-" — this rewrite fires on every
            // arrival at a bare "/mac--{id}" address, which is exactly the
            // shape an entry reference or an old shared link has. Passing the
            // path alone dropped both, so the reader landed on page 1 of the
            // right match with nothing to scroll to.
            navigate(withCurrentQueryAndHash(`/${properSlug}`), { replace: true });
          }
        }
      }
      return;
    }

    // Landing page (bare "/") -> open the topmost Gündem topic ("Son Giriler"
    // order) instead of the first match. Handled BEFORE the matches-length
    // check so the redirect also works on days with an empty match list.
    if (!isSpecialPage && location.pathname === "/") {
      redirectToTopGundemTopic(matches.length > 0 ? matches[0] : null);
      return;
    }

    // No valid match in URL (e.g. an unrecognized slug) -> select first match
    if (matches.length > 0) {
      const firstMatch = matches[0];
      setSelectedMatchId(firstMatch.id);
      if (!isSpecialPage) {
        const slug = generateMatchSlug(firstMatch);
        navigate(`/${slug}`, { replace: true });
      }
    } else {
      setSelectedMatchId(null);
    }
  // location.pathname is included so that programmatic navigation (e.g. clicking a
  // notification that links to a specific entry) correctly updates selectedMatchId.
  // Without it, navigate() changes the URL but the effect never re-fires, leaving
  // the previously selected match still active on screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, loadingMatches, filterKey, location.pathname]);

  // Fetch match from backend if not in list, then redirect to its proper slug URL
  useEffect(() => {
    if (!selectedMatchId) {
      setFetchedMatch(null);
      return;
    }

    // Always fetch fresh match data so isLocked reflects the current DB state.
    // If the match is already in the list, we still fetch to get accurate isLocked.
    //
    // Ticket 1c: two quick selections leave two of these requests in flight, and
    // the header for a match that is NOT in the current list is rendered from
    // fetchedMatch. Without this flag the slower response wins by arriving last
    // and the page shows one match's header above another match's entries. The
    // lock overrides have the same problem — they would describe the wrong match.
    let cancelled = false;

    const fetchMatch = async () => {
      let match: MatchModel | null = null;
      try {
        match = await fetchMatchById(selectedMatchId);
      } catch (err) {
        if (cancelled) return;
        // The match was merged — navigate to the surviving match page
        if (err instanceof MergedTopicError) {
          // The entry anchor still means something on the surviving topic;
          // the page number does not, so only the hash is carried over.
          navigate(`${err.redirectPath}${window.location.hash}`, { replace: true });
          return;
        }
        throw err;
      }
      // A newer selection was made while this request was in flight — its
      // response is the one that must reach the screen.
      if (cancelled) return;
      if (match) {
        // Track isLocked separately — the matches list may have a stale value
        // (e.g. admin locked after the list was loaded)
        setLockedMatchId(match.id);
        setIsLockedOverride(match.isLocked ?? false);
        setLockReasonOverride(match.lockReason ?? null);

        const matchInList = matches.find(m => m.id === selectedMatchId);
        if (matchInList) {
          // Match is in the list — no need to replace URL, just capture isLocked
          setFetchedMatch(null);
        } else {
          // Match not in list — use fetched data and update URL slug
          setFetchedMatch(match);
          const properSlug = generateMatchSlug(match);
          if (!isSpecialPage && location.pathname !== `/${properSlug}`) {
            // Same reason as above: the page and the anchor asked for in the
            // address must survive the slug being filled in.
            navigate(withCurrentQueryAndHash(`/${properSlug}`), { replace: true });
          }
        }
      }
    };
    fetchMatch();
    return () => { cancelled = true; };
  // liveRefreshTick: re-read the selected match after a real-time update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatchId, matches, liveRefreshTick]);

  // Derive selectedMatch: prefer list entry (has entry counts etc.) but merge fresh isLocked
  const selectedMatch = selectedMatchId
    ? (() => {
        const base = matches.find(m => m.id === selectedMatchId) || fetchedMatch;
        if (!base) return null;
        // Overlay the fresh isLocked/lockReason values so lock/unlock is reflected immediately
        const freshLocked = lockedMatchId === selectedMatchId ? isLockedOverride : (base.isLocked ?? false);
        const freshLockReason = lockedMatchId === selectedMatchId ? lockReasonOverride : (base.lockReason ?? null);
        return { ...base, isLocked: freshLocked, lockReason: freshLockReason };
      })()
    : null;

  // After navigation to a match via (#id) ref link, scroll to the target entry
  // once it appears in the DOM (entries load asynchronously after match selection)
  useEffect(() => {
    const hash = location.hash;
    if (!hash.startsWith("#entry-")) return;

    const entryId = hash.slice(1); // e.g. "entry-42"
    let attempts = 0;
    // Raised from 20: on a deep link the target entry only appears after the
    // topic page has resolved its slug, fetched the requested page, and
    // rendered it. 20 tries at 150ms ran out before that on a cold load and
    // the reader was left at the top of the page.
    const maxAttempts = 60;

    const interval = setInterval(() => {
      const el = document.getElementById(entryId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        clearInterval(interval);
      } else if (++attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 150);

    return () => clearInterval(interval);
  }, [location.hash, selectedMatchId]);

  return {
    selectedMatchId,
    selectedMatch,
    isWaitingForInitialMatch: loadingMatches,
    selectAndNavigateToMatch,
  };
};